<template>
  <div class="prt-row">
    <template v-for="segment in segments">
      <div
        v-if="segment.type === 'gap'"
        :key="segment.key"
        class="prt-row__gap"
        :style="segmentStyle(segment)"
      >
        {{ segment.label }}
      </div>
      <EXTooltip
        v-else
        :key="segment.key"
        placement="top"
        :title="segment.label"
        :description="tooltipFor(segment.record)"
        :delay="300"
      >
        <div
          class="prt-row__bar-wrap"
          :style="segmentStyle(segment)"
          @mouseenter="onHover(segment)"
          @focusin="onHover(segment)"
        >
          <PeriodBar
            class="prt-row__bar"
            :state="segment.record.state"
            :label="segment.label"
            :open-ended="segment.openEnded"
            :selectable="true"
            :selected="isSelected(segment)"
            @select="onSelect(segment)"
          />
        </div>
      </EXTooltip>
    </template>
  </div>
</template>

<script setup>
import PeriodBar from "@components/PeriodBar/PeriodBar.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { PERIOD_STATUS_LABEL } from "@components/EmployeeTimeline/employeeTimelineLayout";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

const props = defineProps({
  segments: { type: Array, default: () => [] },
  selectedVersion: { type: [Number, String], default: null },
});

const emit = defineEmits(["select", "bar-hover"]);

function segmentStyle(segment) {
  return {
    position: "absolute",
    left: `${segment.leftPx}px`,
    width: `${segment.widthPx}px`,
  };
}

function normalizeVersion(value) {
  const parsed = Number(String(value ?? "").replace(/^v/i, ""));
  return Number.isFinite(parsed) ? parsed : value;
}

function isSelected(segment) {
  if (props.selectedVersion == null) return false;
  return String(normalizeVersion(segment.record.id)) === String(normalizeVersion(props.selectedVersion));
}

function onSelect(segment) {
  emit("select", normalizeVersion(segment.record.id));
}

function onHover(segment) {
  emit("bar-hover", normalizeVersion(segment.record.id));
}

function tooltipFor(record) {
  const status =
    PERIOD_STATUS_LABEL[record.state] ??
    record.state.charAt(0).toUpperCase() + record.state.slice(1);
  const statePart = record.workflowState ? `${status} · ${record.workflowState}` : status;
  const from = formatFairPayDate(record.effectiveFrom) || "—";
  const openEnded = record.effectiveTo == null || record.effectiveTo === "";
  const to = openEnded ? "—" : formatFairPayDate(record.effectiveTo) || "—";
  return `${statePart}\nEffective from: ${from}\nEffective to: ${to}`;
}
</script>

<style scoped>
.prt-row {
  position: relative;
  width: 100%;
  height: 24px;
}

.prt-row__bar-wrap {
  position: absolute;
  top: 0;
  height: 24px;
}

.prt-row__bar {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
}

.prt-row__gap {
  position: absolute;
  top: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 8px;
  border: 1px dashed #d0d5dd;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 400;
  color: #98a2b3;
  text-align: center;
}
</style>
